import type { AdminUser } from './domain'

// How a user reads in a list, shared by the Users page and the Send page's
// recipient picker, so a filter means the same thing on both.

// Trial badge label + tone. The 14-day Team trial now starts on email
// verification, so an unverified account has no trial yet rather than a lapsed
// one. States:
//   - active (1+ day left): green
//   - waiting for verification: amber
//   - lapsed (0 days, expiresAt in past): subtle grey
//   - no trial recorded (legacy accounts): em-dash
export function trialBadge(u: AdminUser): { label: string; tone: string } {
  if (u.trialActive && u.trialDaysLeft > 0) {
    const days = u.trialDaysLeft
    const tone =
      days <= 3
        ? 'bg-amber-500/15 text-amber-300'
        : 'bg-emerald-500/15 text-emerald-300'
    return { label: `${days}d left`, tone }
  }
  if (u.emailVerified === false && u.trialStartedAt === null) {
    return { label: 'Starts on verify', tone: 'bg-amber-500/10 text-amber-300' }
  }
  if (u.trialExpiresAt !== null) {
    return { label: 'Lapsed', tone: 'bg-white/5 text-slate-400' }
  }
  return { label: '—', tone: 'text-slate-500' }
}


/** Billing, as the Users page's Billing filter reads it. */
export function billingLabel(u: AdminUser): string {
  return u.billing?.paying === true ? 'Paying' : u.billing ? 'Not paying' : 'Unknown'
}

/** Email verification, with "not reported" kept apart from "unverified". */
export function verifiedLabel(u: AdminUser): string {
  return u.emailVerified === true ? 'Verified' : u.emailVerified === false ? 'Unverified' : 'Not reported'
}

const DAY = 86_400_000

/** How long ago, in buckets a filter can offer: Today, This week, This month, Earlier, Never. */
export function agoBucket(at: number | string | null | undefined, now: number = Date.now()): string {
  if (at === null || at === undefined || at === '') return 'Never'
  const t = typeof at === 'number' ? at : Date.parse(at)
  if (!Number.isFinite(t)) return 'Never'
  const age = now - t
  if (age < DAY) return 'Today'
  if (age < 7 * DAY) return 'This week'
  if (age < 30 * DAY) return 'This month'
  return 'Earlier'
}
